/**
 * Maps known third-party domains to cookie categories.
 * Used by the blocking engine to classify scripts/requests before consent.
 *
 * ~150 top domains organized by category (currently 152).
 * Custom per-site mappings from the config API are merged at runtime.
 */
import type { CookieCategory } from "./types";

const ANALYTICS_DOMAINS: readonly string[] = [
	// Google Analytics
	"google-analytics.com",
	"analytics.google.com",
	"googletagmanager.com",
	// Microsoft Clarity
	"clarity.ms",
	// Hotjar
	"hotjar.com",
	"hotjar.io",
	// Matomo / Piwik
	"matomo.cloud",
	"piwik.pro",
	// Mixpanel
	"mixpanel.com",
	"mxpnl.com",
	// Amplitude
	"amplitude.com",
	// Heap
	"heap.io",
	"heapanalytics.com",
	// Segment
	"segment.com",
	"segment.io",
	// Plausible
	"plausible.io",
	// Fathom
	"usefathom.com",
	// PostHog
	"posthog.com",
	// FullStory
	"fullstory.com",
	// LogRocket
	"logrocket.com",
	"logrocket.io",
	// Mouseflow
	"mouseflow.com",
	// Lucky Orange
	"luckyorange.com",
	// Crazy Egg
	"crazyegg.com",
	// StatCounter
	"statcounter.com",
	// New Relic (browser)
	"newrelic.com",
	"nr-data.net",
	// Datadog RUM
	"datadoghq.com",
	"datadoghq.eu",
	// Sentry
	"sentry.io",
	// Bugsnag
	"bugsnag.com",
	// VWO
	"visualwebsiteoptimizer.com",
	// Optimizely
	"optimizely.com",
	// AB Tasty
	"abtasty.com",
	// Chartbeat
	"chartbeat.com",
	"chartbeat.net",
	// Parse.ly
	"parsely.com",
	"parse.ly",
	// Yandex Metrica
	"mc.yandex.ru",
	"mc.yandex.com",
	"metrica.yandex.com",
	"metrika.yandex.ru",
	// Baidu Analytics
	"hm.baidu.com",
	// Adobe Analytics
	"omtrdc.net",
	"demdex.net",
	"2o7.net",
	// Snowplow
	"snowplow.io",
	// Countly
	"count.ly",
	// Smartlook
	"smartlook.com",
	// Inspectlet
	"inspectlet.com",
	// ClickTale
	"clicktale.com",
	"clicktale.net",
];

const MARKETING_DOMAINS: readonly string[] = [
	// Google Ads
	"doubleclick.net",
	"googlesyndication.com",
	"googleadservices.com",
	"googleads.g.doubleclick.net",
	"pagead2.googlesyndication.com",
	"adservice.google.com",
	// Facebook / Meta
	"facebook.net",
	"facebook.com",
	"fbcdn.net",
	"connect.facebook.net",
	// Twitter / X
	"ads-twitter.com",
	"ads-api.twitter.com",
	"analytics.twitter.com",
	"platform.twitter.com",
	"t.co",
	// LinkedIn
	"ads.linkedin.com",
	"linkedin.com",
	"snap.licdn.com",
	// TikTok
	"analytics.tiktok.com",
	"tiktok.com",
	// Pinterest
	"pinimg.com",
	"ct.pinterest.com",
	"ads.pinterest.com",
	// Snap / Snapchat
	"sc-static.net",
	"snap.com",
	"snapchat.com",
	// Outbrain
	"outbrain.com",
	"outbrainsdk.com",
	// Taboola
	"taboola.com",
	"taboolasyndication.com",
	// Criteo
	"criteo.com",
	"criteo.net",
	// AdRoll
	"adroll.com",
	"d.adroll.com",
	// HubSpot
	"hubspot.com",
	"hs-analytics.net",
	"hs-scripts.com",
	"hsforms.net",
	"hsforms.com",
	"hscollectedforms.net",
	// Marketo
	"marketo.net",
	"marketo.com",
	"mktoresp.com",
	// Salesforce / Pardot
	"pardot.com",
	"salesforceliveagent.com",
	// Mailchimp
	"mailchimp.com",
	"chimpstatic.com",
	"list-manage.com",
	// SendGrid
	"sendgrid.net",
	// ActiveCampaign
	"activecampaign.com",
	// Klaviyo
	"klaviyo.com",
	// Intercom
	"intercom.io",
	"intercomcdn.com",
	// Drift
	"drift.com",
	"driftt.com",
	// Zendesk
	"zdassets.com",
	"zendesk.com",
	// Freshworks
	"freshworks.com",
	// Affiliate / tracking
	"shareasale.com",
	"awin1.com",
	"cj.com",
	"impact.com",
	"tradedoubler.com",
	// Ad networks
	"adsrvr.org",
	"rubiconproject.com",
	"pubmatic.com",
	"openx.net",
	"indexww.com",
	"casalemedia.com",
	"amazon-adsystem.com",
	"media.net",
	"bidswitch.net",
	// Retargeting
	"perfectaudience.com",
	"quantserve.com",
	"scorecardresearch.com",
	"bluekai.com",
	"addthis.com",
	"sharethis.com",
];

const FUNCTIONAL_DOMAINS: readonly string[] = [
	// Chat widgets
	"tawk.to",
	"crisp.chat",
	"livechatinc.com",
	"olark.com",
	// Video embeds
	"youtube.com",
	"youtube-nocookie.com",
	"vimeo.com",
	"wistia.com",
	"wistia.net",
	"vidyard.com",
	// Maps
	"maps.googleapis.com",
	"maps.google.com",
	// Social embeds
	"platform.instagram.com",
	// Fonts (usually necessary but some classify as functional)
	"use.typekit.net",
	// reCAPTCHA (often necessary, but listed here for sites that classify it as functional)
	"recaptcha.net",
	// Payment widgets that set tracking cookies
	"js.stripe.com",
	// Push notifications
	"onesignal.com",
	"pushwoosh.com",
	// A/B testing (some classify as functional)
	"cdn.optimizely.com",
	// Customer feedback
	"surveymonkey.com",
	"typeform.com",
	// Translation
	"translate.google.com",
];

/** Flat lookup: domain suffix → category */
const domainMap = new Map<string, CookieCategory>();

function populateMap(): void {
	for (const d of ANALYTICS_DOMAINS) domainMap.set(d, "analytics");
	for (const d of MARKETING_DOMAINS) domainMap.set(d, "marketing");
	for (const d of FUNCTIONAL_DOMAINS) domainMap.set(d, "functional");
}

populateMap();

/**
 * Classify a URL by extracting its hostname and matching against the domain map.
 * Returns the category or null if unknown.
 */
export function classifyUrl(url: string): CookieCategory | null {
	let hostname: string;
	try {
		const base =
			typeof window !== "undefined" && window.location?.href
				? window.location.href
				: "https://localhost";
		hostname = new URL(url, base).hostname;
	} catch {
		return null;
	}

	// Exact match first
	const exactMatch = domainMap.get(hostname);
	if (exactMatch) return exactMatch;

	// Strip subdomains progressively: a.b.example.com → b.example.com → example.com
	const parts = hostname.split(".");
	for (let i = 1; i < parts.length - 1; i++) {
		const suffix = parts.slice(i).join(".");
		const suffixMatch = domainMap.get(suffix);
		if (suffixMatch) return suffixMatch;
	}

	return null;
}

/**
 * Signatures for classifying *inline* scripts (no src) by their source text.
 * Deliberately conservative — each pattern is a vendor's own init call or CDN
 * host, chosen to avoid matching first-party code. Best-effort: inline scripts
 * already executed by the parser can't be un-run, but matching ones are stashed
 * so they don't re-execute and so the external resources they inject stay caught
 * by the src/network layers. Order matters — marketing is checked before the
 * broader analytics patterns (e.g. a tag-manager snippet that also loads ads).
 */
const INLINE_SIGNATURES: ReadonlyArray<{ re: RegExp; category: CookieCategory }> = [
	// Marketing
	{ re: /\bfbq\s*\(/, category: "marketing" },
	{ re: /connect\.facebook\.net/, category: "marketing" },
	{ re: /\b_fbq\b/, category: "marketing" },
	{ re: /\bsnaptr\s*\(/, category: "marketing" },
	{ re: /\bttq\.(load|page|track)\b/, category: "marketing" },
	{ re: /\btwq\s*\(/, category: "marketing" },
	{ re: /\blintrk\s*\(/, category: "marketing" },
	{ re: /_linkedin_partner_id/, category: "marketing" },
	{ re: /\bpintrk\s*\(/, category: "marketing" },
	{ re: /\bobApi\s*\(/, category: "marketing" },
	{ re: /\b_uetq\b/, category: "marketing" },
	// Analytics
	{ re: /\bgtag\s*\(/, category: "analytics" },
	{ re: /GoogleAnalyticsObject/, category: "analytics" },
	{ re: /google-analytics\.com/, category: "analytics" },
	{ re: /googletagmanager\.com/, category: "analytics" },
	{ re: /\b_paq\b/, category: "analytics" },
	{ re: /clarity\.ms|window\.clarity|clarity\s*\(/, category: "analytics" },
	{ re: /_hjSettings|static\.hotjar\.com|\bhj\s*\(/, category: "analytics" },
	{ re: /\bmixpanel\.(init|track)\b/, category: "analytics" },
	{ re: /\bposthog\.(init|capture)\b/, category: "analytics" },
];

/**
 * Classify an inline script by its text content. Returns the matched category,
 * or null when nothing matches (the safe default — leave first-party code alone).
 */
export function classifyInlineScript(content: string | null | undefined): CookieCategory | null {
	if (!content) return null;
	for (const { re, category } of INLINE_SIGNATURES) {
		if (re.test(content)) return category;
	}
	return null;
}

/**
 * Merge custom domain→category mappings from the config API (scan results).
 */
export function mergeDomainMappings(
	mappings: Array<{ domain: string; category: CookieCategory }>,
): void {
	for (const { domain, category } of mappings) {
		domainMap.set(domain, category);
	}
}

/**
 * Check whether a given category is currently consented.
 * Necessary is always consented.
 */
export function isCategoryConsented(
	category: CookieCategory,
	consent: Record<CookieCategory, boolean> | null,
): boolean {
	if (category === "necessary") return true;
	if (!consent) return false;
	return consent[category] === true;
}
